import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import { Empty, Pill, Segment, Textarea, useToast } from '../ui';
import { OUTREACH_EN, OUTREACH_ZH } from '../data/constants';
import { digits, primaryContact } from '../rules';
import { p } from '../routes';
import { tr } from '../i18n';

/**
 * Message all finalists (spec § 5.15).
 *
 * One template, filled per supplier. The placeholders are what make it usable:
 * the buyer edits the message once, and each finalist gets their own contact
 * name, booth and product spliced in — so twenty messages take one edit rather
 * than twenty.
 *
 * Sent / replied is tracked on the supplier (`outreach`), because the whole
 * point is knowing who has not answered.
 */
export default function OutreachScreen() {
  const toast = useToast();
  const { fair, visibleSuppliers, settings, saveSupplier, saveSettings } = useFairTracker();

  const finalists = visibleSuppliers.filter((s) => s.inShortlist);
  const lang = (settings && settings.outreachLang) || 'en';
  const stored = (settings && settings.outreach) || {};
  const template = lang === 'zh' ? stored.zh || OUTREACH_ZH : stored.en || OUTREACH_EN;

  const [draft, setDraft] = useState(template);
  const [langState, setLangState] = useState(lang);

  /* Switching language swaps which stored template is being edited. */
  const switchLang = async (next) => {
    const updated = { ...stored, [langState]: draft };
    setLangState(next);
    setDraft(next === 'zh' ? updated.zh || OUTREACH_ZH : updated.en || OUTREACH_EN);
    try {
      await saveSettings({ ...settings, outreach: updated, outreachLang: next });
    } catch (e) {
      /* a failed preference save must not block messaging */
    }
  };

  const saveTemplate = async () => {
    try {
      await saveSettings({ ...settings, outreach: { ...stored, [langState]: draft } });
      toast('Template saved');
    } catch (e) {
      toast('Could not save');
    }
  };

  const profile = (settings && settings.profile) || {};

  /** Fill the placeholders for one supplier (spec § 5.15). */
  const fill = (supplier) => {
    const contact = primaryContact(supplier);
    const product =
      (supplier.products || []).find((x) => x.decision === 'Strong') || (supplier.products || [])[0] || {};
    const name = (contact && contact.name) || '';

    /* No contact name is the normal case at a fair — you photographed the card
       but never typed the name in. English has a natural filler; Chinese does
       not, and "您好 there，" in an otherwise Chinese message is worse than no
       name at all. So outside English the placeholder is removed along with
       the space in front of it, leaving "您好，…". */
    const withContact =
      name
        ? draft.replace(/\{contact\}/g, name)
        : langState === 'en'
        ? draft.replace(/\{contact\}/g, 'there')
        : draft.replace(/[  　]?\{contact\}/g, '');

    return withContact
      .replace(/\{me\}/g, profile.name || '')
      .replace(/\{company\}/g, profile.company || '')
      .replace(/\{fair\}/g, (fair && fair.name) || '')
      .replace(/\{booth\}/g, supplier.booth || '')
      .replace(/\{product\}/g, product.name || '')
      .replace(/\{qty\}/g, product.moq || '')
      .replace(/\{port\}/g, product.fobPort || 'Shenzhen');
  };

  const mark = async (supplier, patch) => {
    try {
      await saveSupplier({ ...supplier, outreach: { ...(supplier.outreach || {}), ...patch } });
    } catch (e) {
      toast('Could not save');
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast('Message copied');
    } catch (e) {
      toast('Could not copy — select the text instead');
    }
  };

  return (
    <div className="ft-page">
      <div className="ft-title">
        <h1 className="ft-h1">{tr("Message all finalists")}</h1>
        <p className="ft-sub" style={{ margin: 0 }}>
          {tr(`${finalists.length} finalist${finalists.length === 1 ? '' : 's'}`)} ·{' '}
          {tr('one edit, personalised per supplier')}
        </p>
      </div>

      {!finalists.length && (
        <Empty
          title={tr("No finalists yet")}
          action={
            <Link className="ft-btn" to={p('/shortlist')}>
              {tr("Open the shortlist")}
            </Link>
          }
        >
          {tr("Mark a product Strong and the supplier appears here.")}
        </Empty>
      )}

      <section className="ft-card">
        <h2 className="ft-h2">{tr("Your message")}</h2>
        <Segment
          value={langState}
          onChange={switchLang}
          options={[
            { value: 'en', label: 'English' },
            { value: 'zh', label: '中文' },
          ]}
        />
        <p className="ft-hint" style={{ marginTop: 8 }}>
          {tr('Placeholders:')} {'{contact} {me} {company} {fair} {booth} {product} {qty} {port}'}
        </p>
        <Textarea value={draft} onChange={setDraft} rows={7} />
        <button type="button" className="ft-btn ft-btn-green ft-btn-block" onClick={saveTemplate}>
          {tr("Save template")}
        </button>
      </section>

      <div className="ft-list" style={{ marginTop: 12 }}>
        {finalists.map((s) => {
          const contact = primaryContact(s);
          const text = fill(s);
          const wa = digits((contact && (contact.whatsapp || contact.phone)) || '');
          const out = s.outreach || {};

          return (
            <article className="ft-card" key={s.id}>
              <header className="ft-planhead">
                <div>
                  <h2 className="ft-h2">{s.company}</h2>
                  <p className="ft-sub" style={{ margin: 0 }}>
                    {[contact && contact.name, s.booth ? `Booth ${s.booth}` : null]
                      .filter(Boolean)
                      .join(' · ') || 'No contact saved'}
                  </p>
                </div>
                {out.replied ? (
                  <Pill tone="strong">{tr("Replied")}</Pill>
                ) : out.sentAt ? (
                  <Pill tone="maybe">{tr("Sent")}</Pill>
                ) : null}
              </header>

              <p className="ft-prewrap ft-outreach-preview">{text}</p>

              <div className="ft-btnrow">
                {wa ? (
                  <a
                    className="ft-btn ft-btn-sm ft-btn-primary"
                    href={`https://wa.me/${wa}?text=${encodeURIComponent(text)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => mark(s, { sentAt: Date.now() })}
                  >
                    <Icon name="MessageCircle" size={14} /> WhatsApp
                  </a>
                ) : (
                  <span className="ft-hint">{tr("No WhatsApp number saved")}</span>
                )}
                {contact && contact.email && (
                  <a
                    className="ft-btn ft-btn-sm"
                    href={`mailto:${contact.email}?subject=${encodeURIComponent(
                      `${(fair && fair.name) || 'Trade fair'} follow-up`,
                    )}&body=${encodeURIComponent(text)}`}
                    onClick={() => mark(s, { sentAt: Date.now() })}
                  >
                    <Icon name="Mail" size={14} /> {tr("Email")}
                  </a>
                )}
                <button type="button" className="ft-btn ft-btn-sm" onClick={() => copy(text)}>
                  <Icon name="Copy" size={14} /> {tr("Copy")}
                </button>

                {!out.sentAt ? (
                  <button
                    type="button"
                    className="ft-btn ft-btn-sm"
                    onClick={() => mark(s, { sentAt: Date.now() })}
                  >
                    {tr("Mark sent")}
                  </button>
                ) : !out.replied ? (
                  <button
                    type="button"
                    className="ft-btn ft-btn-sm ft-btn-green"
                    onClick={() => mark(s, { replied: true })}
                  >
                    {tr("Mark replied")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ft-btn ft-btn-sm"
                    onClick={() => mark(s, { replied: false })}
                  >
                    {tr("Not replied")}
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
